import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, CheckCircle2, ShieldCheck, Briefcase, ClipboardCheck, Eye, Send, Handshake,
  LogIn, Clock, XCircle, Loader2, MapPin, Banknote,
} from "lucide-react";

const INDUSTRIES = ["Technology", "Banking & Finance", "Materials & Chemicals", "Consulting", "Marketing", "Legal", "Manufacturing", "Real Estate", "Human Resources", "Other"];
const EMPLOYMENT = ["Full-time", "Part-time", "Contract", "Internship", "Freelance"];

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

const STEPS = [
  { icon: LogIn, title: "1. Sign in & request access", text: "Sign in with Google and request recruiter access with your company details." },
  { icon: ShieldCheck, title: "2. We approve you", text: "Our team whitelists verified recruiters. You need our permission to post." },
  { icon: Eye, title: "3. Post & we approve each role", text: "Post vacancies from your portal — each one is approved before it goes live." },
];

// ---- Request recruiter access ----
const RequestAccessForm = ({ onDone }) => {
  const [f, setF] = useState({ company_name: "", company_website: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.company_name) { toast.error("Company name is required"); return; }
    setSubmitting(true);
    try { await api.post("/recruiter/request", f); toast.success("Access requested — pending approval"); onDone(); }
    catch (e) { toast.error("Could not submit request"); }
    finally { setSubmitting(false); }
  };
  return (
    <Card className="p-6 sm:p-8 space-y-5" data-testid="request-access-form">
      <div className="flex items-center gap-2"><Building2 className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-xl">Request recruiter access</h3></div>
      <p className="text-sm text-muted-foreground">Tell us about your company. Once our team approves you, you can post vacancies.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Company name *"><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="ra-company" /></F>
        <F label="Company website"><Input placeholder="https://" value={f.company_website} onChange={(e) => set("company_website", e.target.value)} data-testid="ra-website" /></F>
        <F label="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ra-phone" /></F>
      </div>
      <Button onClick={submit} disabled={submitting} size="lg" className="rounded-full w-full font-semibold" data-testid="ra-submit">
        {submitting ? "Submitting…" : "Request access"}
      </Button>
    </Card>
  );
};

// ---- Post a vacancy (approved recruiters) ----
const VacancyForm = ({ onPosted }) => {
  const [f, setF] = useState({
    role_needed: "", profile_name: "", industry: "", employment_type: "",
    experience_required: "", location: "", ctc: "", openings: "1", description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.role_needed) { toast.error("Job role is required"); return; }
    setSubmitting(true);
    try {
      await api.post("/recruiter/vacancies", { ...f, openings: parseInt(f.openings) || 1 });
      toast.success("Vacancy submitted for approval!");
      setF({ role_needed: "", profile_name: "", industry: "", employment_type: "", experience_required: "", location: "", ctc: "", openings: "1", description: "" });
      onPosted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
    finally { setSubmitting(false); }
  };
  return (
    <Card className="p-6 sm:p-8 space-y-5" data-testid="vacancy-form">
      <div className="flex items-center gap-2"><Send className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-xl">Post a vacancy</h3></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <F label="Job role *"><Input placeholder="e.g. Backend Engineer" value={f.role_needed} onChange={(e) => set("role_needed", e.target.value)} data-testid="vac-role" /></F>
        <F label="Profile / designation"><Input placeholder="e.g. Senior Software Engineer" value={f.profile_name} onChange={(e) => set("profile_name", e.target.value)} data-testid="vac-profile" /></F>
        <F label="Industry">
          <Select value={f.industry} onValueChange={(v) => set("industry", v)}>
            <SelectTrigger data-testid="vac-industry"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Employment type">
          <Select value={f.employment_type} onValueChange={(v) => set("employment_type", v)}>
            <SelectTrigger data-testid="vac-employment"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{EMPLOYMENT.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Experience required"><Input placeholder="e.g. 3–5 years" value={f.experience_required} onChange={(e) => set("experience_required", e.target.value)} data-testid="vac-exp" /></F>
        <F label="CTC / salary"><Input placeholder="e.g. ₹12–18 LPA" value={f.ctc} onChange={(e) => set("ctc", e.target.value)} data-testid="vac-ctc" /></F>
        <F label="Location"><Input placeholder="e.g. Bangalore / Remote" value={f.location} onChange={(e) => set("location", e.target.value)} data-testid="vac-location" /></F>
        <F label="No. of openings"><Input type="number" min="1" value={f.openings} onChange={(e) => set("openings", e.target.value)} data-testid="vac-openings" /></F>
      </div>
      <F label="Job description & candidate requirements">
        <Textarea rows={4} placeholder="Describe the role, responsibilities and ideal candidate…" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="vac-desc" />
      </F>
      <div className="rounded-xl bg-accent/10 border border-accent/30 px-4 py-3 text-xs text-foreground flex items-start gap-2" data-testid="vac-approval-note">
        <ShieldCheck className="h-4 w-4 text-accent shrink-0 mt-0.5" />
        Every vacancy is reviewed and requires our approval before it appears on the public Jobs board.
      </div>
      <Button onClick={submit} disabled={submitting} size="lg" className="rounded-full w-full font-semibold" data-testid="vac-submit">
        {submitting ? "Submitting…" : <><Send className="h-4 w-4 mr-1.5" /> Submit vacancy for approval</>}
      </Button>
    </Card>
  );
};

// ---- Approved recruiter dashboard ----
const RecruiterDashboard = () => {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get("/recruiter/vacancies"); setVacancies(res.data); }
    catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-8" data-testid="recruiter-dashboard">
      <VacancyForm onPosted={load} />
      <div>
        <h3 className="font-heading font-semibold text-xl mb-3 flex items-center gap-2"><Briefcase className="h-5 w-5 text-primary" /> My vacancies</h3>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : vacancies.length === 0 ? (
          <Card className="p-8 text-center border-dashed" data-testid="no-vacancies"><p className="text-sm text-muted-foreground">You haven't posted any vacancies yet.</p></Card>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {vacancies.map((v) => (
              <Card key={v.id} className="p-5" data-testid={`my-vacancy-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h4 className="font-heading font-semibold">{v.role_needed}</h4>
                    {v.profile_name && <p className="text-xs text-muted-foreground">{v.profile_name}</p>}
                  </div>
                  {v.published
                    ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid={`vacancy-status-${v.id}`}>Live</Badge>
                    : <Badge variant="secondary" data-testid={`vacancy-status-${v.id}`}>Pending review</Badge>}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                  {v.location && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {v.location}</span>}
                  {v.ctc && <span className="flex items-center gap-1"><Banknote className="h-3.5 w-3.5" /> {v.ctc}</span>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const Recruiters = () => {
  const { user, loading, login, checkAuth } = useAuth();

  const signIn = () => {
    sessionStorage.setItem("post_login_redirect", "/recruiters");
    login();
  };

  const renderPortal = () => {
    if (loading) return <div className="py-12 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;

    if (!user) {
      return (
        <Card className="p-10 text-center" data-testid="recruiter-signin">
          <LogIn className="h-11 w-11 text-primary mx-auto mb-3" />
          <h3 className="font-heading font-bold text-2xl">Sign in to post vacancies</h3>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">Recruiters sign in with Google, request access, and once we approve you can post and manage your open roles.</p>
          <Button onClick={signIn} size="lg" className="rounded-full font-semibold mt-6" data-testid="recruiter-signin-btn">
            <LogIn className="h-4 w-4 mr-1.5" /> Sign in with Google
          </Button>
        </Card>
      );
    }

    const status = user.recruiter_status || "none";
    if (status === "none") return <RequestAccessForm onDone={checkAuth} />;
    if (status === "pending") return (
      <Card className="p-10 text-center" data-testid="recruiter-pending">
        <Clock className="h-11 w-11 text-amber-500 mx-auto mb-3" />
        <h3 className="font-heading font-bold text-2xl">Access pending approval</h3>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto">Thanks! Your recruiter access is under review. We'll approve verified recruiters shortly — check back soon.</p>
      </Card>
    );
    if (status === "rejected") return (
      <Card className="p-10 text-center" data-testid="recruiter-rejected">
        <XCircle className="h-11 w-11 text-red-500 mx-auto mb-3" />
        <h3 className="font-heading font-bold text-2xl">Access not approved</h3>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto">Your recruiter access request wasn't approved. Please contact our team if you think this is a mistake.</p>
      </Card>
    );
    return <RecruiterDashboard />;
  };

  return (
    <div className="font-body" data-testid="recruiters-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-5 pt-16 pb-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium mb-5">
          <Handshake className="h-3.5 w-3.5 text-accent" /> For Recruiters & Companies
        </div>
        <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight max-w-3xl">Hire the right people. Post your vacancy.</h1>
        <p className="mt-4 text-muted-foreground text-base sm:text-lg max-w-2xl">
          Approved recruiters and companies can post open roles with full details — job role, candidate profile and CTC.
          Every posting is <span className="font-semibold text-foreground">reviewed and approved by our team</span> before it goes live.
        </p>
        <div className="mt-6">
          <Link to="/jobs"><Button variant="outline" className="rounded-full font-semibold" data-testid="rec-view-jobs"><Briefcase className="h-4 w-4 mr-1.5" /> View live job board</Button></Link>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 pb-8">
        <div className="grid md:grid-cols-3 gap-4">
          {STEPS.map((s, i) => (
            <Card key={i} className="p-6" data-testid={`rec-step-${i}`}>
              <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center mb-4"><s.icon className="h-5 w-5 text-primary" /></div>
              <h3 className="font-heading font-semibold">{s.title}</h3>
              <p className="text-sm text-muted-foreground mt-1.5">{s.text}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Portal (state-aware) */}
      <section className="max-w-3xl mx-auto px-5 py-8">{renderPortal()}</section>

      {/* Disclaimer */}
      <section className="max-w-3xl mx-auto px-5 pb-16">
        <div className="rounded-xl bg-secondary/60 border border-border px-4 py-3 text-xs text-muted-foreground text-center" data-testid="rec-disclaimer">
          <strong className="text-foreground">Please note:</strong> Posting a vacancy does not guarantee applications or hires. We facilitate the connection between recruiters and candidates.
        </div>
      </section>
    </div>
  );
};

export default Recruiters;
